import React from "react";
import glamorous from "glamorous";

import config from "../config";

import Section from "../components/Section";
import FeatureButton from "../components/FeatureButton";
import { Hero, HeroTitle, HeroDescription, HeroImage } from "../components/Hero";
import { Testimonials, Testimonial, TestimonialQuote, TestimonialAuthor } from "../components/Testimonials";
import { Funnel, FunnelTitle } from "../components/Funnel";

import AllInOnePlaceImage from "../assets/all-in-one-place.png";
import OmniboxTimelineImage from "../assets/omnibox-timeline.png";
import IntegrationImage from "../assets/integration.png";

const NarrowContainer = glamorous.div(({ theme }) => ({
  maxWidth: theme.main.maxWidths.default,
  width: "90%",
  [theme.main.mediaQueries.mobile]: {
    flexDirection: "column"
  }
}));

const FeatureImage = glamorous.div(({ fullWidth = false, crampedAlignment = "center", theme }) => ({
  backgroundSize: "auto 100%",
  backgroundRepeat: "no-repeat",
  backgroundPositionY: "center",
  backgroundPositionX: "center",
  height: 325,
  width: fullWidth ? "100%" : "55%",
  [theme.main.mediaQueries.tablet]: {
    height: 275,
    backgroundPositionX: fullWidth ? "center" : crampedAlignment,
    width: fullWidth ? "100%" : "55%"
  },
  [theme.main.mediaQueries.mobile]: {
    backgroundSize: "auto 80%",
    backgroundPositionX: crampedAlignment,
    width: "100%"
  }
}));

const FeatureImageGradient = glamorous.div(({ color = { r: 255, g: 255, b: 255 }, visibleSide = "right" }) => ({
  background: visibleSide === "none"
    ? `linear-gradient(to right, rgba(${color.r},${color.g},${color.b},1) 0%,rgba(${color.r},${color.g},${color.b},0) 15%,rgba(${color.r},${color.g},${color.b},0) 85%,rgba(${color.r},${color.g},${color.b},1) 100%)`
    : `linear-gradient(to ${visibleSide}, rgba(${color.r},${color.g},${color.b},1) 0%,rgba(${color.r},${color.g},${color.b},0) 15%,rgba(${color.r},${color.g},${color.b},0) 100%)`,
  height: "100%",
  width: "100%"
}));

const FeatureDescription = glamorous.div(({ textAlign = "left", theme }) => ({
  margin: "auto",
  textAlign,
  width: "40%",
  [theme.main.mediaQueries.tablet]: {
    width: "60%"
  },
  [theme.main.mediaQueries.mobile]: {
    textAlign: "center",
    width: "90%"
  }
}));

const IndexPage = ({ onToggleContactForm }) => (
  <div>
    <Hero glam={{ overhang: true }}>
      <NarrowContainer>
        <HeroTitle>{config.shortDescription}</HeroTitle>
        <glamorous.Div display="flex">
          <div>
            <HeroDescription>
              {config.longDescription}
            </HeroDescription>
            <FeatureButton onClick={onToggleContactForm}>Arrange a demo</FeatureButton>
          </div>
          {/*<a href="#">
            <HeroVideo />
          </a>*/}
          <HeroImage />
        </glamorous.Div>
      </NarrowContainer>
    </Hero>
    <Section glam={{ layout: "center" }} css={{ paddingTop: "6rem", paddingBottom: 0}}>
      <NarrowContainer>
        <FeatureDescription textAlign="center">
          <glamorous.H2>
            See everything in one place
          </glamorous.H2>
          <p>
            View all your resources, users and their bookings without having to navigate three layers down. Like a real workdesk should be.
          </p>
        </FeatureDescription>
        <FeatureImage
          fullWidth={true}
          crampedAlignment="left"
          css={{
            backgroundImage: `url('${AllInOnePlaceImage}')`,
            backgroundPositionY: "bottom"
          }}
        >
          <FeatureImageGradient color={{ r: 245, g: 249, b: 250}} visibleSide="left" />
        </FeatureImage>
      </NarrowContainer>
    </Section>
    <Section glam={{ layout: "left" }} css={{ paddingLeft: 0, borderTop: "1px solid #f0f0f0" }}>
      <FeatureImage
        crampedAlignment="right"
        css={{
          backgroundImage: `url('${OmniboxTimelineImage}')`,
          marginRight: "5%"
        }}
      >
        <FeatureImageGradient />
      </FeatureImage>
      <FeatureDescription>
        <glamorous.H2>
          Work quickly, work efficiently
        </glamorous.H2>
        <p>
          Search for anything with the omnibox, and visualise results on the timeline to get the full picture, even for bookings with 20+ items that end at different times.
        </p>
      </FeatureDescription>
    </Section>
    <Section glam={{ layout: "right" }}>
      <NarrowContainer css={{ display: "flex", alignItems: "center" }}>
        <FeatureDescription>
          <glamorous.H2>
            Migrate easily from existing systems
          </glamorous.H2>
          <p>
            Manta works well with existing systems. Integrate with your institution&apos;s sign-in service, and copy over all your resources and booking history from WebCheckout or Excel files.
          </p>
        </FeatureDescription>
        <FeatureImage
          containWhenSmall={true}
          css={{
            backgroundImage: `url('${IntegrationImage}')`,
            backgroundPositionX: "center"
          }}
        />
      </NarrowContainer>
    </Section>
    <Section css={{ background: "#fdfdfd", borderTop: "1px solid #f0f0f0" }}>
      <NarrowContainer>
        <glamorous.H2 css={{ textAlign: "center" }}>
          What our clients say
        </glamorous.H2>
        <Testimonials>
          <Testimonial>
            <TestimonialQuote >
              Making back-to-back bookings for our documentation space is so much easier than before. The timeline in Manta is easy to use and saves us a lot of time.
            </TestimonialQuote>
            <TestimonialAuthor>
              <strong>Kevin Dossinger</strong><br />
              <small>AAP IT Solutions at Cornell University</small>
            </TestimonialAuthor>
          </Testimonial>
          <Testimonial>
            <TestimonialQuote css={{borderLeftColor: "#a9dce3"}}>
              The customer support team at Manta is fantastic. They are always willing and eager to assist with questions. Manta is an intuitive system that continues to evolve and improve to meet the needs of its users.
            </TestimonialQuote>
            <TestimonialAuthor>
              <strong>Lindsey Glover</strong><br />
              <small>Advanced Digital Media Services Coordinator</small><br />
              <small>Cornell University</small>
            </TestimonialAuthor>
          </Testimonial>
        </Testimonials>
      </NarrowContainer>
    </Section>
    <Funnel>
      <FunnelTitle css={{ paddingRight: "3rem" }}>Try Manta Equipment now</FunnelTitle>
      <FeatureButton onClick={onToggleContactForm}>Arrange a demo</FeatureButton>
    </Funnel>
  </div>
);

export default IndexPage;



// WEBPACK FOOTER //
// ./src/pages/index.js